import React, { useState, useEffect } from 'react';
import { Wrench, Plus, Search, Edit, Eye, ChevronLeft, Save, Trash2 } from 'lucide-react';
import { supabase } from './supabaseClient';

// Separate Form Component - Prevents focus loss
const ServiceForm = ({ 
  formData, 
  onFormDataChange, 
  onSave, 
  onCancel, 
  saving, 
  isEditing 
}) => {
  // Local state to prevent parent re-renders from affecting inputs
  const [localFormData, setLocalFormData] = useState(formData);

  // Update local state when parent form data changes
  useEffect(() => {
    setLocalFormData(formData);
  }, [formData]);

  // Handle input changes locally first
  const handleChange = (e) => {
    const { name, value } = e.target;
    const newData = { ...localFormData, [name]: value };
    setLocalFormData(newData);
    onFormDataChange(newData);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg">
      {/* Header */}
      <div className={`${isEditing ? 'bg-purple-500' : 'bg-green-500'} text-white p-6 rounded-t-xl`}>
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            {isEditing ? <Edit size={32} /> : <Plus size={32} />}
            <h2 className="text-2xl font-bold">
              {isEditing ? 'Edit Service' : 'New Service'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <ChevronLeft size={20} />
            <span>Back</span>
          </button>
        </div>
      </div>

      {/* Form Fields */}
      <div className="p-6">
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Name *
            </label>
            <input
              type="text"
              name="service_name"
              value={localFormData.service_name || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="e.g., Clean asphalt surface"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              name="category"
              value={localFormData.category || ''}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">Select Category</option>
              <option value="Surface Preparation">Surface Preparation</option>
              <option value="Sealing">Sealing</option>
              <option value="Paving">Paving</option>
              <option value="Crack Filling">Crack Filling</option>
              <option value="Repairs">Repairs</option>
              <option value="Line Painting">Line Painting</option>
              <option value="Maintenance">Maintenance</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={localFormData.description || ''}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Brief description of the service (optional)"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={localFormData.status || 'Active'}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Seasonal">Seasonal</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sort Order
              </label>
              <input
                type="number"
                name="sort_order"
                value={localFormData.sort_order || ''}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={localFormData.notes || ''}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Internal notes about this service..."
            />
          </div>

          <div className="flex justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSave(localFormData)}
              disabled={saving}
              className={`px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                saving
                  ? 'bg-gray-400 cursor-not-allowed'
                  : isEditing
                  ? 'bg-purple-500 hover:bg-purple-600'
                  : 'bg-green-500 hover:bg-green-600'
              } text-white`}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>{isEditing ? 'Update Service' : 'Save Service'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Services Management Component
function ServicesManagement() {
  const [activeView, setActiveView] = useState('list'); // 'list', 'detail', 'new', 'edit'
  const [selectedService, setSelectedService] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    service_name: '',
    category: '',
    description: '',
    status: 'Active',
    sort_order: '',
    notes: ''
  });

  // Load services from Supabase
  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .order('sort_order', { ascending: true, nullsLast: true })
        .order('service_name', { ascending: true });

      if (error) throw error;
      setServices(data || []);
    } catch (error) {
      console.error('Error loading services:', error);
      alert('Error loading services. Please check your Supabase connection.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      service_name: '',
      category: '',
      description: '',
      status: 'Active',
      sort_order: '',
      notes: ''
    });
  };

  const handleFormDataChange = (newData) => {
    setFormData(newData);
  };

  const handleSave = async (dataToSave) => {
    // Validate required fields
    if (!dataToSave.service_name) {
      alert('Please enter a service name');
      return;
    }
    
    try {
      setSaving(true);
      
      // Clean up the data before saving
      const cleanData = {
        ...dataToSave,
        sort_order: dataToSave.sort_order === '' || dataToSave.sort_order === null ? null : parseInt(dataToSave.sort_order)
      };
      
      if (activeView === 'edit' && selectedService) {
        // Update existing service
        const { error } = await supabase
          .from('services')
          .update(cleanData)
          .eq('id', selectedService.id);
        
        if (error) throw error;
        alert('Service updated successfully!');
      } else {
        // Create new service
        const { error } = await supabase
          .from('services')
          .insert([cleanData]);
        
        if (error) throw error;
        alert('Service created successfully!');
      }
      
      // Reload services and return to list
      await loadServices();
      setActiveView('list');
      resetForm();
      
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Error saving service: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setActiveView('list');
    resetForm();
  };

  const deleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', serviceId);

      if (error) throw error;
      
      alert('Service deleted successfully!');
      await loadServices();
      setActiveView('list');
    } catch (error) {
      console.error('Error deleting service:', error);
      alert('Error deleting service: ' + error.message);
    }
  };

  // Filter services based on search and category
  const filteredServices = services.filter(service => {
    const matchesSearch = service.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.category?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || service.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Get unique categories for filter
  const categories = [...new Set(services.map(service => service.category))].filter(Boolean);

  // Show form for new or edit
  if (activeView === 'new' || activeView === 'edit') {
    return (
      <ServiceForm
        formData={formData}
        onFormDataChange={handleFormDataChange}
        onSave={handleSave}
        onCancel={handleCancel}
        saving={saving}
        isEditing={activeView === 'edit'}
      />
    );
  }

  // Service Detail View
  if (activeView === 'detail') {
    return (
      <div className="bg-white rounded-xl shadow-lg">
        {/* Header */}
        <div className="bg-purple-500 text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Wrench size={32} />
              <div>
                <h2 className="text-2xl font-bold">{selectedService?.service_name}</h2>
                <p className="text-purple-100">{selectedService?.category || 'No category'}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setFormData({
                    service_name: selectedService.service_name,
                    category: selectedService.category || '',
                    description: selectedService.description || '',
                    status: selectedService.status,
                    sort_order: selectedService.sort_order || '',
                    notes: selectedService.notes || ''
                  });
                  setActiveView('edit');
                }}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <Edit size={20} />
                <span>Edit</span>
              </button>
              <button
                onClick={() => deleteService(selectedService.id)}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <Trash2 size={20} />
                <span>Delete</span>
              </button>
              <button
                onClick={() => setActiveView('list')}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <ChevronLeft size={20} />
                <span>Back</span>
              </button>
            </div>
          </div>
        </div>

        {/* Service Details */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Service Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Service Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Service Name:</span>
                  <span className="font-medium">{selectedService?.service_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Category:</span>
                  <span className="font-medium">{selectedService?.category || 'No category'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`font-medium ${
                    selectedService?.status === 'Active' ? 'text-green-600' : 
                    selectedService?.status === 'Seasonal' ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {selectedService?.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Service ID:</span>
                  <span className="font-medium">#{selectedService?.id}</span>
                </div>
              </div>
            </div>

            {/* Additional Details */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Additional Details</h3>
              <div className="space-y-3">
                {selectedService?.sort_order && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sort Order:</span>
                    <span className="font-medium">{selectedService.sort_order}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Created:</span>
                  <span className="font-medium">{new Date(selectedService?.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>

          {selectedService?.description && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Description</h3>
              <p className="text-gray-600 bg-gray-50 p-4 rounded-lg">{selectedService.description}</p>
            </div>
          )}

          {selectedService?.notes && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Notes</h3>
              <p className="text-gray-600 bg-gray-50 p-4 rounded-lg">{selectedService.notes}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Services List View (Default)
  return (
    <div className="bg-white rounded-xl shadow-lg">
      {/* Header */}
      <div className="bg-purple-500 text-white p-6 rounded-t-xl">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Wrench size={32} />
            <div>
              <h2 className="text-2xl font-bold">Services Management</h2>
              <p className="text-purple-100">{services.length} services</p>
            </div>
          </div>
          <button
            onClick={() => {
              resetForm();
              setActiveView('new');
            }}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Plus size={20} />
            <span>New Service</span>
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="p-6 border-b">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search services by name, description, or category..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Services List */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading services...</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredServices.map(service => (
              <div key={service.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between gap-4">
                  <div className="bg-purple-100 p-2 rounded-full flex-shrink-0">
                    <Wrench className="text-purple-600" size={24} />
                  </div>

                  <div className="flex-shrink-0 w-52">
                    <h3 className="text-base font-semibold text-gray-800">{service.service_name}</h3>
                  </div>

                  <div className="flex-shrink-0 w-36">
                    <p className="text-base text-gray-600 truncate">{service.category || 'No category'}</p>
                  </div>

                  <div className="flex-1 min-w-0">
                    {service.description && (
                      <p className="text-base text-gray-600 truncate">{service.description}</p>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <span className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
                      service.status === 'Active' ? 'bg-green-100 text-green-800' :
                      service.status === 'Seasonal' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {service.status}
                    </span>
                    {service.sort_order && (
                      <span className="text-gray-500 text-sm whitespace-nowrap">Order: {service.sort_order}</span>
                    )}
                  </div>

                  <div className="flex space-x-1 flex-shrink-0">
                    <button
                      onClick={() => {
                        setSelectedService(service);
                        setActiveView('detail');
                      }}
                      className="bg-purple-500 hover:bg-purple-600 text-white p-2 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedService(service);
                        setFormData({
                          service_name: service.service_name,
                          category: service.category || '',
                          description: service.description || '',
                          status: service.status,
                          sort_order: service.sort_order || '',
                          notes: service.notes || ''
                        });
                        setActiveView('edit');
                      }}
                      className="bg-green-500 hover:bg-green-600 text-white p-2 rounded-lg transition-colors"
                      title="Edit Service"
                    >
                      <Edit size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filteredServices.length === 0 && (
          <div className="text-center py-8">
            <Wrench className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500">
              {searchTerm || categoryFilter ? 'No services found matching your criteria.' : 'No services yet. Add your first service!'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ServicesManagement;